import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "./theme-toggle";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { publicDisplayName } from "@/lib/public-display-name";
import {
  Menu,
  LogIn,
  LogOut,
  User,
  Search,
  Home,
  BookOpen,
  Building2,
  Phone,
  Users,
  ShieldCheck,
  Settings,
  LayoutDashboard,
} from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SearchDialog } from "./search-dialog";
import { BRAND } from "@/lib/brand-config";

interface PrimaryNavItem {
  href: string;
  label: string;
  icon: typeof Home;
  matches: (loc: string) => boolean;
  testId: string;
}

const primaryNav: PrimaryNavItem[] = [
  {
    href: "/",
    label: "Home",
    icon: Home,
    matches: (loc) => loc === "/",
    testId: "nav-home",
  },
  {
    href: "/projects",
    label: "Projects",
    icon: Building2,
    matches: (loc) => loc === "/projects" || loc.startsWith("/projects/"),
    testId: "nav-projects",
  },
  {
    href: "/knowledge-base",
    label: "Knowledge Base",
    icon: BookOpen,
    matches: (loc) => loc === "/knowledge-base",
    testId: "nav-knowledge-base",
  },
  {
    href: "/about",
    label: "About",
    icon: Users,
    matches: (loc) => loc === "/about",
    testId: "nav-about",
  },
];

type AgentMeProbe = {
  id: string;
  contactEmail?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
} | null;

export function Header() {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, isLoading } = useAuth();
  // Partner cookies are invisible to /api/auth/me. Probe so public pages
  // still offer Sign Out instead of a dead-end "Sign In" while a partner
  // session is live (apply blocks with sign_out_first otherwise).
  const { data: agentMe, isLoading: agentLoading } = useQuery<AgentMeProbe>({
    queryKey: ["/api/agent/me"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !isLoading && !user,
    staleTime: 30_000,
    retry: false,
  });

  const partnerLabel = agentMe
    ? [agentMe.contactFirstName, agentMe.contactLastName].filter(Boolean).join(" ").trim() ||
      agentMe.contactEmail ||
      "Partner"
    : null;

  const headerLabel = user
    ? publicDisplayName(user.name, { role: user.role, email: user.email })
    : partnerLabel
      ? publicDisplayName(partnerLabel, { email: agentMe?.contactEmail })
      : null;

  const signedIn = !!user || !!agentMe;
  const sessionLoading = isLoading || (!user && agentLoading);

  const signOut = () => {
    window.location.href = "/logout";
  };

  return (
    <header className="sticky top-0 z-50 w-full glass-header pt-safe">
      <div className="container mx-auto flex h-14 sm:h-16 items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4">
        <Link href="/" className="flex items-center shrink-0 transition-smooth" data-testid="link-logo">
          <picture className="hidden dark:block">
            <source media="(min-width: 768px)" srcSet="/logo-navbar.svg" />
            <img src="/logo-navbar-mobile.svg" alt={BRAND.siteName} className="h-[30px] md:h-9 w-auto" />
          </picture>
          <picture className="block dark:hidden">
            <source media="(min-width: 768px)" srcSet="/logo-navbar-light.svg" />
            <img src="/logo-navbar-mobile-light.svg" alt={BRAND.siteName} className="h-[30px] md:h-9 w-auto" />
          </picture>
        </Link>

        <nav aria-label="Main" className="hidden lg:flex items-center gap-1" data-testid="nav-desktop">
          {primaryNav.map((item) => (
            <Link key={item.href} href={item.href}>
              <Button
                variant={item.matches(location) ? "secondary" : "ghost"}
                size="sm"
                className="gap-1.5 font-medium tracking-tight"
                data-testid={item.testId}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Button>
            </Link>
          ))}

          <Link href="/schedule-call">
            <Button
              size="sm"
              className="ml-2 gap-1.5 font-semibold btn-gold-3d text-brand-dark-bg"
              data-testid="nav-schedule-consultation"
            >
              <Phone className="h-4 w-4" />
              Schedule a Consultation
            </Button>
          </Link>
        </nav>

        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="touch-target"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            data-testid="button-search"
          >
            <Search className="h-4 w-4" />
          </Button>
          {!sessionLoading && (
            signedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="button-user-menu">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">{headerLabel}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                    {user?.email || agentMe?.contactEmail || "Signed in"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {user?.role === "admin" ? (
                    <Link href="/admin">
                      <DropdownMenuItem className="cursor-pointer" data-testid="button-admin-portal">
                        <Settings className="h-4 w-4 mr-2" />
                        Admin Portal
                      </DropdownMenuItem>
                    </Link>
                  ) : agentMe ? (
                    <Link href="/agent/dashboard">
                      <DropdownMenuItem className="cursor-pointer" data-testid="button-partner-portal">
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        Partner Portal
                      </DropdownMenuItem>
                    </Link>
                  ) : (
                    <>
                      <Link href="/dashboard">
                        <DropdownMenuItem className="cursor-pointer" data-testid="button-dashboard">
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          Dashboard
                        </DropdownMenuItem>
                      </Link>
                      <Link href="/dashboard/accreditation">
                        <DropdownMenuItem className="cursor-pointer" data-testid="button-verification-status">
                          <ShieldCheck className="h-4 w-4 mr-2" />
                          Confirm Eligibility
                        </DropdownMenuItem>
                      </Link>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} data-testid="button-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link href="/login" className="hidden sm:block">
                <Button variant="ghost" size="sm" className="gap-2" data-testid="button-header-login">
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Button>
              </Link>
            )
          )}

          <ThemeToggle />

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="touch-target" aria-label="Open menu" data-testid="button-mobile-menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 glass-nav border-l-0 overflow-y-auto">
              <div className="flex items-center mb-6 pb-4 border-b border-border/50">
                <img src="/logo-navbar.svg" alt={BRAND.siteName} className="h-9 w-auto hidden dark:block" />
                <img src="/logo-navbar-light.svg" alt={BRAND.siteName} className="h-9 w-auto block dark:hidden" />
              </div>
              <nav className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full justify-start gap-3 touch-target"
                  onClick={() => { setMobileOpen(false); setTimeout(() => setSearchOpen(true), 150); }}
                  data-testid="button-mobile-search"
                >
                  <Search className="h-5 w-5" />
                  Search...
                </Button>

                {primaryNav.map((item) => (
                  <Link key={item.href} href={item.href}>
                    <Button
                      variant={item.matches(location) ? "secondary" : "ghost"}
                      className="w-full justify-start gap-3 touch-target"
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-${item.testId}`}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </Button>
                  </Link>
                ))}

                <Link href="/schedule-call">
                  <Button
                    size="lg"
                    className="w-full justify-start gap-3 touch-target btn-gold-3d text-brand-dark-bg font-semibold"
                    onClick={() => setMobileOpen(false)}
                    data-testid="mobile-nav-schedule-consultation"
                  >
                    <Phone className="h-5 w-5" />
                    Schedule a Consultation
                  </Button>
                </Link>

                <div className="pt-4 mt-2 border-t border-border/50 space-y-2">
                  {signedIn ? (
                    <>
                      <div className="px-3 py-3 text-sm text-muted-foreground glass-card rounded-lg">
                        Signed in as <span className="font-medium text-foreground">{headerLabel}</span>
                      </div>
                      <Link href={user?.role === "admin" ? "/admin" : agentMe ? "/agent/dashboard" : "/dashboard"}>
                        <Button
                          variant="outline"
                          size="lg"
                          className="w-full justify-start gap-3 touch-target"
                          onClick={() => setMobileOpen(false)}
                          data-testid="mobile-nav-dashboard"
                        >
                          <LayoutDashboard className="h-5 w-5" />
                          {user?.role === "admin" ? "Admin Portal" : agentMe ? "Partner Portal" : "Dashboard"}
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full justify-start gap-3 touch-target"
                        onClick={() => { signOut(); setMobileOpen(false); }}
                        data-testid="mobile-nav-sign-out"
                      >
                        <LogOut className="h-5 w-5" />
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <Link href="/login">
                      <Button variant="outline" size="lg" className="w-full justify-start gap-3 touch-target" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-sign-in">
                        <LogIn className="h-5 w-5" />
                        Sign In
                      </Button>
                    </Link>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
